import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { t } from 'i18next';
import { getPropertyAccessorForEventAssetCreator } from '@/tableDefinitionEditor/eventAssetCreator.utilities';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import {
  setDoTableReload,
  setEvaluationResults,
  setRequestProgress,
  setRowDataIsLoading,
  setScrollToColumnId,
  setSelectedColumns,
  setTableDefinition,
} from '@/tableDefinitionEditor/tableDefinition.actions';
import { CSV_TYPES, EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  addOrUpdateColumnDefinition,
  addOrUpdateColumnDefinitions,
  extractChildKeyFromString,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import {
  setColumnToEditId,
  setInitialColumnRule,
  setInitialRuleParameterOverrides,
  setSelectedColumnOption,
  setSelectedTemplateOption,
  setShowColumnProperties,
} from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { doTrack } from '@/track/trackProxy.service';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionBulkEditInputV1';
import type { EditorType } from '@/tableDefinitionEditor/tableDefinition.types';
import type { TrackInformation } from '@/track/track.types';
import type { ColumnDefinitionBulkEditInputV1 } from '@seeqdev/api/model/ColumnDefinitionBulkEditInputV1';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';

const columnConfigurationTrackCategory = (editorType: EditorType) =>
  editorType === EDITOR_TYPES.SCALING ? 'Scaling Table Column Configuration' : 'Tree Builder';

export const resetRowLoadProgressAndSetRowLoading = (tableDefinitionId: string, editorType: EditorType) => {
  setRequestProgress(editorType, {
    id: tableDefinitionId,
    progress: '0' as unknown as Record<string, unknown>,
    meterInformation: undefined,
    timingInformation: undefined,
  });
  setRowDataIsLoading(editorType, true);
};

export const modifyColumnsToIncludeEventAssetCreator = (
  columnInputs: ColumnDefinitionBulkEditInputV1[],
  editorType: EditorType,
) => {
  const tableDefinitionStore = getTableDefinitionStore(editorType);
  const { tableType, name, id } = tableDefinitionStore;
  if (CSV_TYPES.includes(tableType)) {
    return columnInputs;
  }

  const modifiedColumns = columnInputs.map((column) => {
    const isUUIDColumn = column.columnType === ColumnTypeEnum.UUID;
    const onlyOneRule = column.columnRules.length === 1;
    const eventPropertyRule = column.columnRules.find((rule) => rule.eventProperty);
    const eventPropertyName = eventPropertyRule?.eventProperty?.propertyName;
    const hasEventPropertyRule = eventPropertyName !== undefined && eventPropertyName !== '';
    const isDescendantEventProperty =
      eventPropertyName !== undefined && extractChildKeyFromString(eventPropertyName) !== undefined;
    const isItemIDColumn = column.columnName === SeeqNames.MaterializedTables.ItemIdColumn;

    const shouldModify =
      isItemIDColumn || (isUUIDColumn && onlyOneRule && hasEventPropertyRule && !isDescendantEventProperty);
    if (!shouldModify) {
      return column;
    }
    if (eventPropertyRule) {
      const description = id
        ? t('SCALING.RULES.EVENT_ASSET_CREATOR.GENERATED_RULE_DESCRIPTION_WITH_ID', { name, id })
        : t('SCALING.RULES.EVENT_ASSET_CREATOR.GENERATED_RULE_DESCRIPTION');
      const propertyName = getPropertyAccessorForEventAssetCreator(eventPropertyRule.eventProperty?.propertyName) || '';
      return {
        ...column,
        columnRules: [
          ...column.columnRules,
          {
            eventAssetCreator: {
              propertyName: propertyName ?? '',
              description,
            },
          },
        ],
      };
    }
    return column;
  });
  return modifiedColumns;
};
export const addOrUpdateColumns = async (
  columnDefinitions: ColumnDefinitionBulkEditInputV1[],
  tableDefinitionId: string,
  editorType: EditorType,
  skipRecompute?: boolean,
) => {
  resetRowLoadProgressAndSetRowLoading(tableDefinitionId, editorType);
  const columns = modifyColumnsToIncludeEventAssetCreator(columnDefinitions, editorType);
  const output = await addOrUpdateColumnDefinitions(columns, tableDefinitionId, skipRecompute);
  if (output) {
    setTableDefinition(editorType, output.tableDefinition);
    setEvaluationResults(editorType, output.computeResults);
    setDoTableReload(editorType, true);
    setScrollToColumnId(
      editorType,
      output.tableDefinition.columnDefinitions?.find(
        (column) => column.columnName === columns[columns.length - 1].columnName,
      )?.id || '',
    );

    const trackCategory = columnConfigurationTrackCategory(editorType);
    const trackInformation: TrackInformation = {
      numberOfColumns: columns.length,
    };
    doTrack(trackCategory, columns.some((col) => col.columnId) ? 'Edit Columns' : 'Add Columns', trackInformation);
  }
  return output;
};

export const onCloseColumnConfig = (editorType: EditorType) => {
  setSelectedColumnOption(editorType, undefined);
  setColumnToEditId(editorType, undefined);
  setShowColumnProperties(editorType, false);
  setSelectedColumns(editorType, []);
  setInitialColumnRule(editorType, undefined);
  setInitialRuleParameterOverrides(editorType, undefined);
};

export const onCloseTemplateConfig = (editorType: EditorType) => {
  setSelectedTemplateOption(editorType, undefined);
};

export const addOrUpdateColumn = async (
  editorType: EditorType,
  tableDefinitionId: string,
  columnAsInput: ColumnDefinitionInputV1,
  columnId?: string,
  skipRuleCompute = false,
  skipTracking = false,
  closeColumnConfig = true,
  includeDetailedResults = true,
) => {
  resetRowLoadProgressAndSetRowLoading(tableDefinitionId, editorType);
  const modifiedInput = modifyColumnsToIncludeEventAssetCreator([columnAsInput], editorType)[0];
  const output = await addOrUpdateColumnDefinition(
    tableDefinitionId,
    modifiedInput,
    columnId,
    skipRuleCompute,
    includeDetailedResults,
  );
  const trackCategory = columnConfigurationTrackCategory(editorType);
  if (output) {
    if (!skipTracking) {
      const trackInformation: TrackInformation = {
        numberOfRules: columnAsInput.columnRules.length,
      };
      columnAsInput.columnRules.forEach((rule) => {
        const ruleName = Object.keys(rule).find((key) => key)!;
        trackInformation[ruleName] = ruleName;
      });
      doTrack(trackCategory, columnId ? 'Edit Column' : 'Add Column', trackInformation);
    }
    setTableDefinition(editorType, output.tableDefinition);
    setEvaluationResults(editorType, includeDetailedResults ? output.computeResults : {});
    setDoTableReload(editorType, true);
    setScrollToColumnId(
      editorType,
      output.tableDefinition.columnDefinitions?.find((column) => column.columnName === columnAsInput.columnName)?.id ||
        '',
    );
    if (closeColumnConfig) {
      onCloseColumnConfig(editorType);
    }
  }
  return output;
};
